/**
 * Paper-day receipt books (F4-B3.a): register a printed book, take it back. Limits follow
 * uc-core's CreateReceiptBookDto (bookNo /^[A-Za-z0-9-]{1,20}$/, slip numbers ≥ 1, last ≥ first).
 */

/** A printed book holds tens of slips; anything bigger is almost certainly a typo. */
export const MAX_SLIPS_PER_BOOK = 500
const MAX_SLIP_NO = 2_147_483_647 // Postgres int

export interface BookForm {
  bookNo: string
  firstSlipNo: number | null
  lastSlipNo: number | null
}

export type BookFormErrors = Partial<Record<keyof BookForm, string>>

export function validateBookForm(f: BookForm): BookFormErrors {
  const e: BookFormErrors = {}
  const bookNo = f.bookNo.trim()
  if (!bookNo) e.bookNo = 'Required'
  else if (!/^[A-Za-z0-9-]{1,20}$/.test(bookNo)) e.bookNo = 'Letters, digits and dashes only, 20 at most'
  if (f.firstSlipNo === null) e.firstSlipNo = 'Required'
  else if (f.firstSlipNo < 1 || f.firstSlipNo > MAX_SLIP_NO) e.firstSlipNo = 'Must be 1 or more'
  if (f.lastSlipNo === null) e.lastSlipNo = 'Required'
  else if (f.lastSlipNo < 1 || f.lastSlipNo > MAX_SLIP_NO) e.lastSlipNo = 'Must be 1 or more'
  if (!e.firstSlipNo && !e.lastSlipNo && f.firstSlipNo !== null && f.lastSlipNo !== null) {
    if (f.lastSlipNo < f.firstSlipNo) e.lastSlipNo = 'Must not be before the first slip'
    else if (slipCount(f.firstSlipNo, f.lastSlipNo) > MAX_SLIPS_PER_BOOK)
      e.lastSlipNo = `That is ${slipCount(f.firstSlipNo, f.lastSlipNo)} slips — a book has ${MAX_SLIPS_PER_BOOK} at most`
  }
  return e
}

export const slipCount = (first: number, last: number) => last - first + 1

/**
 * Typed list of blank slips → numbers: "446-449, 452 455" → [446,447,448,449,452,455].
 * Accepts commas, spaces or new lines between items and "a-b" / "a–b" ranges. Every number must
 * be inside the book's range.
 */
export function parseSlipList(
  text: string,
  first: number,
  last: number,
): { slips: number[]; error: string | null } {
  const out = new Set<number>()
  const items = text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)
  for (const item of items) {
    const m = /^(\d+)(?:[-–](\d+))?$/.exec(item)
    if (!m) return { slips: [], error: `“${item}” is not a slip number or range` }
    const a = Number(m[1])
    const b = m[2] === undefined ? a : Number(m[2])
    if (b < a) return { slips: [], error: `Range ${item} runs backwards` }
    if (a < first || b > last) return { slips: [], error: `${item} is not in this book (${first}–${last})` }
    for (let n = a; n <= b; n++) out.add(n)
  }
  return { slips: [...out].sort((x, y) => x - y), error: null }
}

/** Numbers → compact ranges for display: [1,2,3,5,7,8] → "1–3, 5, 7–8". */
export function formatSlipRanges(slips: readonly number[]): string {
  const sorted = [...new Set(slips)].sort((a, b) => a - b)
  const parts: string[] = []
  let i = 0
  while (i < sorted.length) {
    let j = i
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j]! + 1) j++
    parts.push(i === j ? String(sorted[i]) : `${sorted[i]}–${sorted[j]}`)
    i = j + 1
  }
  return parts.join(', ')
}
